import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Trophy, Mail, MessageCircle, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { money, shortDate } from '@/lib/buyerMessaging';
import { NewCampaignV2Dialog } from '@/components/buyersV2/NewCampaignV2Dialog';
import { TIER_STYLE } from '@/lib/buyersV2';

const scoreColor = (s) => s >= 70 ? 'bg-emerald-500' : s >= 45 ? 'bg-amber-500' : 'bg-gray-400';

function MatchRow({ b, rank, checked, onToggle, onOpen }) {
  return (
    <div data-testid={`best-buyer-${b.id}`} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${checked ? 'border-[#0073ea]/40 bg-blue-50/50' : 'border-gray-200 hover:bg-gray-50'}`}>
      <Checkbox checked={checked} onCheckedChange={() => onToggle(b.id)} data-testid={`best-buyer-check-${b.id}`} />
      <div className={`h-9 w-9 rounded-full ${scoreColor(b.score)} text-white flex items-center justify-center text-sm font-bold shrink-0`} data-testid={`best-buyer-score-${b.id}`}>{b.score}</div>
      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onOpen(b.id)}>
        <div className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
          {rank === 0 && <Trophy className="h-3.5 w-3.5 text-amber-500" />}{b.entity_name || b.name}
          {b.tier && <Badge variant="outline" className={`text-[10px] py-0 ${TIER_STYLE[b.tier] || ''}`}>{b.tier}</Badge>}
        </div>
        <p className="text-[11px] text-gray-500 truncate">{b.reasons?.join(' · ') || 'Weak match'}</p>
      </div>
      <div className="text-right text-[11px] text-gray-500 hidden sm:block shrink-0">
        <p>{b.total_purchases_24mo || 0} buys/24mo · top {b.top_zip || '—'}</p>
        <p>Last {shortDate(b.last_purchase_date)} · {money(b.last_purchase_price)}</p>
      </div>
    </div>
  );
}

export function BestBuyersCard({ lead, onUseInBlast }) {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [dialog, setDialog] = useState(null);

  useEffect(() => {
    if (!lead?.id) return;
    API.get(`/leads/${lead.id}/matched-buyers`).then(r => setMatches(r.data.items)).catch(() => setMatches([]));
  }, [lead?.id, lead?.property_zip, lead?.property_city, lead?.asking_price, lead?.marketing_price, lead?.property_type]);

  const toggle = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const shown = showAll ? matches : matches.slice(0, 5);
  const chosen = matches.filter(b => selected.includes(b.id));

  return (
    <Card className="bg-white border-gray-200 shadow-sm" data-testid="best-buyers-card">
      <CardContent className="p-5 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-500" /> Best Buyers for this Property</p>
            <p className="text-xs text-gray-500">Scored on zip/city buys, price range, beds/baths/sqft, property type, recency and tier</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" variant="outline" data-testid="best-select-top5" onClick={() => setSelected(matches.slice(0, 5).map(b => b.id))} disabled={!matches.length}>Select top 5</Button>
            <Button size="sm" data-testid="best-email-btn" disabled={!chosen.length} onClick={() => setDialog('email')} className="bg-[#0073ea] hover:bg-[#0060c2] text-white"><Mail className="h-3.5 w-3.5 mr-1" /> Email</Button>
            <Button size="sm" data-testid="best-text-btn" disabled={!chosen.length} onClick={() => setDialog('text')} className="bg-[#25D366] hover:bg-[#1ebe5b] text-white"><MessageCircle className="h-3.5 w-3.5 mr-1" /> Text</Button>
            <Button size="sm" data-testid="best-campaign-btn" onClick={() => setDialog('campaign')} disabled={!matches.length} className="bg-gray-900 text-white">Campaign to top matches</Button>
            <Button size="sm" variant="outline" data-testid="best-blast-btn" disabled={!chosen.length} onClick={() => onUseInBlast(selected)}><Send className="h-3.5 w-3.5 mr-1" /> Deal Blast</Button>
          </div>
        </div>
        {matches.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4" data-testid="no-best-buyers">No matching buyers yet. Add the property city/zip and import buyer purchase history.</p>
        ) : (
          <div className="space-y-1.5">
            {shown.map((b, i) => <MatchRow key={b.id} b={b} rank={i} checked={selected.includes(b.id)} onToggle={toggle} onOpen={(id) => navigate(`/buyers/${id}`)} />)}
            {matches.length > 5 && (
              <button data-testid="best-show-all" onClick={() => setShowAll(!showAll)} className="w-full text-xs text-[#0073ea] hover:underline flex items-center justify-center gap-1 pt-1">
                {showAll ? <><ChevronUp className="h-3 w-3" /> Show less</> : <><ChevronDown className="h-3 w-3" /> Show all {matches.length}</>}
              </button>
            )}
          </div>
        )}
      </CardContent>
      <NewCampaignV2Dialog open={dialog === 'campaign'} onOpenChange={v => !v && setDialog(null)} preset={{ lead_id: lead.id }} />
      <NewCampaignV2Dialog open={dialog === 'email' || dialog === 'text'} onOpenChange={v => !v && setDialog(null)} preset={{ lead_id: lead.id, buyer_ids: chosen.map(b => b.id), channel: dialog === 'text' ? 'sms' : 'email' }} />
    </Card>
  );
}
